import { useEffect, useRef, useState } from "react"
import { Alert, Box, Button, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import Quill from "quill"
import equal from "fast-deep-equal"
import type {
  EditorValidity,
  MessageContent,
  SlotOccurrence,
  VisualEdit,
} from "../types"
import {
  collectSlotOccurrences,
  groupSlotOccurrences,
  isWholeSlotMarker,
} from "../model/slots"
import { parseMessageContent } from "../markup/conversion"
import { TelegramUrlButtonsEditor } from "../buttons/telegramUrlButtonsEditor"
import { FormatToolbar } from "./formatToolbar"
import { MessageLinkDialog } from "./messageLinkDialog"
import { useTelegramEditor } from "./useTelegramEditor"
import { documentToDelta } from "./quillAdapter"
import { sourceRangeToDisplay, type EditorRange } from "./sourceTokens"
import {
  findLinkSelection,
  linkEdit,
  safeDisplay,
  semanticSelection,
  type EditorSlot,
  type LinkSelection,
} from "./linkEditing"

export interface TelegramMessageEditorProps {
  value: MessageContent
  onChange: (value: MessageContent) => void
  onValidityChange?: (validity: EditorValidity) => void
  readOnly?: boolean
  slots?: EditorSlot[]
  /** Allocate an identity only; the occurrence is created atomically on apply. */
  onCreateSlot?: () => string
  onSelectOccurrence?: (occurrence: SlotOccurrence) => void
  /** One-shot preview request, fenced to the exact source that produced it. */
  openOccurrence?: {
    id: number
    occurrence: SlotOccurrence
    content: MessageContent
  }
  /** Host navigates to a separate empty-version page; never resets this draft. */
  onRecover?: () => void
  onBack?: () => void
  media?: {
    id: string
    kind: "photo" | "video"
    name?: string
    previewUrl?: string
  }[]
  onAddMedia?: () => void
  onRemoveMedia?: (id: string) => void
  onMoveMedia?: (from: number, to: number) => void
  keyboardAllowed?: boolean
}

const rejectedMessage =
  "Это оформление нельзя изменить в выбранном месте. Выберите другой фрагмент."

export function TelegramMessageEditor(props: TelegramMessageEditorProps) {
  const editor = useTelegramEditor(props)
  const { content, quill, presentation } = editor
  const root = useRef<HTMLDivElement>(null)
  const savedRange = useRef<EditorRange | null>(null)
  const linkSerial = useRef(0)
  const openedRequest = useRef<number>()
  const returnRange = useRef<EditorRange | null>(null)
  const [link, setLink] = useState<{
    key: number
    selection: LinkSelection
    baseline: MessageContent
    anchor: { getBoundingClientRect: () => DOMRect; contextElement: Element }
  } | null>(null)
  const [linkError, setLinkError] = useState("")
  const parsed = parseMessageContent(content)
  const occurrences = collectSlotOccurrences(content)
  const slots =
    props.slots ??
    groupSlotOccurrences(occurrences).map((group) => ({
      id: group.slotId,
      label: group.label,
      occurrences: group.occurrences,
    }))
  const contextOccurrences = [...occurrences]
  for (const slot of slots)
    for (const occurrence of slot.occurrences)
      if (!contextOccurrences.some((item) => equal(item, occurrence)))
        contextOccurrences.push(occurrence)

  useEffect(() => {
    if (!quill) return
    quill.root.setAttribute("role", "textbox")
    quill.root.setAttribute("aria-multiline", "true")
    quill.root.setAttribute("aria-label", "Текст сообщения")
    quill.root.setAttribute(
      "aria-readonly",
      String(Boolean(props.readOnly || !presentation?.parsed)),
    )
    quill.root.querySelectorAll(".telegram-link").forEach((element) => {
      element.setAttribute(
        "aria-label",
        `Настроить ссылку: ${safeDisplay(element.textContent ?? "")}`,
      )
    })
    const remember = (range: EditorRange | null) => {
      if (range) savedRange.current = range
    }
    quill.on("selection-change", remember)
    return () => {
      quill.off("selection-change", remember)
    }
  }, [quill, presentation, props.readOnly])

  const getSelection = () => quill?.getSelection() ?? savedRange.current
  const openLink = (selection?: LinkSelection) => {
    if (!presentation || !parsed.ok || !quill) return
    const range = selection
      ? sourceRangeToDisplay(selection.range, presentation.mapping)
      : (getSelection() ?? { index: presentation.sentinel.index, length: 0 })
    const clamped = {
      index: Math.min(range.index, presentation.sentinel.index),
      length: Math.min(
        range.length,
        Math.max(0, presentation.sentinel.index - range.index),
      ),
    }
    const bounds = quill.getBounds(clamped.index, clamped.length)
    setLink({
      key: linkSerial.current++,
      selection:
        selection ??
        findLinkSelection(
          parsed.value,
          semanticSelection(clamped, presentation, parsed.value),
        ),
      baseline: content,
      anchor: {
        contextElement: quill.container,
        getBoundingClientRect: () => {
          const container = quill.container.getBoundingClientRect()
          return bounds
            ? new DOMRect(
                container.left + bounds.left,
                container.top + bounds.top,
                bounds.width,
                bounds.height,
              )
            : container
        },
      },
    })
    returnRange.current = clamped
    setLinkError("")
  }
  const closeLink = () => {
    setLink(null)
    quill?.focus()
    if (returnRange.current) quill?.setSelection(returnRange.current, "silent")
  }
  const applyLinkEdit = (edit: VisualEdit, after: EditorRange) => {
    if (!link || props.readOnly) return
    if (!equal(link.baseline, content)) {
      setLinkError("Сообщение изменилось. Выберите ссылку снова.")
      return
    }
    const result = editor.applyEdit(edit, {
      before: returnRange.current,
      after: (next) => {
        const visual = documentToDelta(next)
        return visual.ok
          ? sourceRangeToDisplay(after, visual.value.mapping)
          : null
      },
    })
    if (!result?.ok) setLinkError(rejectedMessage)
    return result
  }
  const saveLink = (text: string, url: string) => {
    if (!link) return false
    const result = applyLinkEdit(linkEdit(link.selection, text, url), {
      index: link.selection.range.index + (text || url).length,
      length: 0,
    })
    if (!result?.ok) return false
    const visual = documentToDelta(result.value)
    if (visual.ok)
      returnRange.current = sourceRangeToDisplay(
        { index: link.selection.range.index + (text || url).length, length: 0 },
        visual.value.mapping,
      )
    return true
  }
  const removeLink = () => {
    if (!link) return
    const { selection } = link
    const operations: VisualEdit["operations"] =
      selection.entityIndex !== undefined
        ? [{ kind: "removeEntity", index: selection.entityIndex }]
        : [
            {
              kind: "text",
              from: selection.range.index,
              to: selection.range.index + selection.range.length,
              text: "",
            },
          ]
    const result = applyLinkEdit(
      { operations },
      {
        index: selection.range.index,
        length:
          selection.entityIndex !== undefined ? selection.range.length : 0,
      },
    )
    if (result?.ok) {
      const visual = documentToDelta(result.value)
      if (visual.ok)
        returnRange.current = sourceRangeToDisplay(
          {
            index: selection.range.index,
            length:
              selection.entityIndex !== undefined ? selection.range.length : 0,
          },
          visual.value.mapping,
        )
      closeLink()
    }
  }
  const selectOccurrence = (occurrence: SlotOccurrence) => {
    props.onSelectOccurrence?.(occurrence)
    if (occurrence.document.startsWith("button")) {
      const [, , row, button] = occurrence.path.split("/")
      const target = root.current?.querySelector<HTMLElement>(
        `[data-telegram-button="${row}.${button}"]`,
      )
      target?.focus()
      target?.scrollIntoView?.({ block: "nearest" })
      return
    }
    if (!parsed.ok || !presentation || !quill) return
    let range: EditorRange | undefined
    if (occurrence.document === "entity-url") {
      const entity =
        parsed.value.entities[Number(occurrence.path.split("/")[3])]
      if (entity) range = { index: entity.offset, length: entity.length }
    } else if (content.formatting.kind === "markup") {
      const entry = parsed.value.mapping.find(
        (item) =>
          (occurrence.offset ?? 0) >= item.sourceRange.offset &&
          (occurrence.offset ?? 0) <
            item.sourceRange.offset + item.sourceRange.length,
      )
      if (entry)
        range = {
          index: entry.textRange.offset,
          length: entry.textRange.length,
        }
    } else
      range = { index: occurrence.offset ?? 0, length: occurrence.length ?? 0 }
    if (!range) return
    const display = sourceRangeToDisplay(range, presentation.mapping)
    returnRange.current = display
    quill.setSelection(display, "silent")
    quill.root.scrollIntoView?.({ block: "nearest" })
    return range
  }
  // Selection is not an edit. Wait for Quill, then consume once; old source or
  // occurrence requests must never open a different place after replacement.
  const incomingContext = useRef({
    occurrences,
    selectOccurrence,
    parsed,
    openLink,
  })
  incomingContext.current = { occurrences, selectOccurrence, parsed, openLink }
  useEffect(() => {
    const { occurrences, selectOccurrence, parsed, openLink } =
      incomingContext.current
    const request = props.openOccurrence
    if (
      !request ||
      !quill ||
      !presentation ||
      openedRequest.current === request.id
    )
      return
    openedRequest.current = request.id
    if (
      !equal(request.content, props.value) ||
      !equal(request.content, content)
    )
      return
    // Captions are presentation, not identity (HTML references may be decoded
    // in the preview). Fence by the complete source plus the exact location.
    const occurrence = occurrences.find(
      (item) =>
        item.slotId === request.occurrence.slotId &&
        item.document === request.occurrence.document &&
        item.path === request.occurrence.path &&
        item.offset === request.occurrence.offset &&
        item.length === request.occurrence.length,
    )
    if (!occurrence) return
    if (occurrence.document.startsWith("button")) {
      setLink(null)
      const [, , row, button] = occurrence.path.split("/")
      root.current
        ?.querySelector<HTMLElement>(
          `[data-telegram-button="${row}.${button}"]`,
        )
        ?.click()
    } else {
      const range = selectOccurrence(occurrence)
      if (range && parsed.ok) openLink(findLinkSelection(parsed.value, range))
    }
  }, [props.openOccurrence, props.value, quill, presentation, content])
  const openElement = (target: Element) => {
    if (!quill || !presentation || !parsed.ok) return
    const blot = Quill.find(target)
    if (!blot || blot instanceof Quill) return
    const range = { index: quill.getIndex(blot), length: 0 }
    openLink(
      findLinkSelection(
        parsed.value,
        semanticSelection(range, presentation, parsed.value),
      ),
    )
  }
  const stale = Boolean(link && !equal(link.baseline, content))
  return (
    <Stack
      ref={root}
      spacing={2}
      onKeyDown={(event) => {
        if (link && event.key === "Escape") {
          event.preventDefault()
          event.stopPropagation()
          closeLink()
        }
      }}
      sx={{ minWidth: 0, maxWidth: "100%", overflowWrap: "anywhere" }}
    >
      <Box
        sx={{
          border: 1,
          borderColor: (theme) => alpha(theme.palette.text.primary, 0.14),
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <FormatToolbar
          quill={quill}
          onLink={() => openLink()}
          getSelection={getSelection}
          readOnly={props.readOnly}
          valid={parsed.ok}
          canUndo={editor.canUndo}
          canRedo={editor.canRedo}
        />
        {!parsed.ok && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            Не удалось открыть форматирование этой версии
            <Stack direction="row" useFlexGap flexWrap="wrap">
              {props.onBack && (
                <Button onClick={props.onBack}>Назад к версиям</Button>
              )}
              {props.onRecover && (
                <Button onClick={props.onRecover}>Создать пустую версию</Button>
              )}
            </Stack>
          </Alert>
        )}
        {!!editor.rejected.length && !link && (
          <Alert severity="warning">{rejectedMessage}</Alert>
        )}
        <Box
          ref={editor.mountRef}
          onKeyDown={(event) => {
            if (
              (event.ctrlKey || event.metaKey) &&
              event.key.toLowerCase() === "k"
            ) {
              event.preventDefault()
              openLink()
            }
            const target =
              event.target instanceof Element
                ? event.target.closest(".telegram-link, .telegram-slot")
                : null
            if (target && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault()
              openElement(target)
            }
          }}
          onClick={(event) => {
            const clicked = event.target as Node
            if (!quill || !presentation || !quill.root.contains(clicked)) return
            const element =
              clicked instanceof Element ? clicked : clicked.parentElement
            const target = element?.closest(".telegram-link, .telegram-slot")
            if (target) {
              event.preventDefault()
              openElement(target)
              return
            }
            const range = quill.getSelection()
            if (!range || range.length || !parsed.ok) return
            const selection = findLinkSelection(
              parsed.value,
              semanticSelection(range, presentation, parsed.value),
            )
            if (selection.url) {
              event.preventDefault()
              openLink(selection)
            }
          }}
          sx={{
            minWidth: 0,
            display: parsed.ok ? "block" : "none",
            "& .ql-editor": {
              minHeight: 160,
              p: 2,
              outline: "none",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            },
            "& .ql-editor:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: -2,
            },
            "& .ql-editor p": { margin: 0 },
            "& .telegram-link": {
              color: "primary.main",
              textDecoration: "underline",
              cursor: "pointer",
            },
            "& .telegram-slot, & .telegram-shared-link": {
              color: "secondary.main",
              backgroundColor: (theme) =>
                alpha(theme.palette.secondary.main, 0.08),
              borderRadius: 0.5,
              cursor: "pointer",
            },
            "& .telegram-slot": { px: 0.5 },
            "& .telegram-slot:focus, & .telegram-link:focus": {
              outline: "2px solid",
              outlineColor: "secondary.main",
            },
          }}
        />
      </Box>
      <Stack spacing={1} aria-label="Медиа сообщения">
        {(
          props.media ??
          content.mediaIds.map((id) => ({ id, kind: "photo" as const }))
        ).map((media, index, items) => (
          <Stack
            key={media.id}
            direction="row"
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            spacing={1}
          >
            <Typography>
              {"name" in media && media.name
                ? safeDisplay(media.name)
                : `${media.kind === "photo" ? "Фото" : "Видео"} ${index + 1}`}
            </Typography>
            {props.onMoveMedia && (
              <>
                <Button
                  disabled={props.readOnly || index === 0}
                  aria-label={`Медиа ${index + 1} вверх`}
                  onClick={() => {
                    if (!props.readOnly) props.onMoveMedia?.(index, index - 1)
                  }}
                >
                  ↑
                </Button>
                <Button
                  disabled={props.readOnly || index === items.length - 1}
                  aria-label={`Медиа ${index + 1} вниз`}
                  onClick={() => {
                    if (!props.readOnly) props.onMoveMedia?.(index, index + 1)
                  }}
                >
                  ↓
                </Button>
              </>
            )}
            {props.onRemoveMedia && (
              <Button
                disabled={props.readOnly}
                aria-label={`Удалить медиа ${index + 1}`}
                onClick={() => {
                  if (!props.readOnly) props.onRemoveMedia?.(media.id)
                }}
              >
                Удалить
              </Button>
            )}
          </Stack>
        ))}
        {props.onAddMedia && (
          <Button
            disabled={props.readOnly}
            onClick={() => {
              if (!props.readOnly) props.onAddMedia?.()
            }}
          >
            Добавить медиа
          </Button>
        )}
      </Stack>
      <TelegramUrlButtonsEditor
        value={content.buttons}
        onChange={(buttons) => {
          if (!props.readOnly) props.onChange({ ...content, buttons })
        }}
        slots={slots}
        occurrences={contextOccurrences}
        onSelectOccurrence={selectOccurrence}
        onCreateSlot={props.onCreateSlot}
        readOnly={props.readOnly}
        keyboardAllowed={props.keyboardAllowed ?? content.mediaIds.length < 2}
      />
      {link && (
        <MessageLinkDialog
          key={link.key}
          text={link.selection.text}
          url={link.selection.url}
          anchor={link.anchor}
          slots={slots}
          occurrences={contextOccurrences}
          onCreateSlot={props.onCreateSlot}
          onSelectOccurrence={selectOccurrence}
          readOnly={props.readOnly || stale}
          error={
            stale ? "Сообщение изменилось. Выберите ссылку снова." : linkError
          }
          onSave={saveLink}
          onClose={closeLink}
          onRemove={
            link.selection.entity?.type === "text_link" ||
            isWholeSlotMarker(link.selection.url)
              ? removeLink
              : undefined
          }
          removeLabel={
            link.selection.entity?.type === "text_link"
              ? "Убрать ссылку"
              : "Удалить подстановку"
          }
        />
      )}
    </Stack>
  )
}
